import { Bar, Doughnut, Line } from "react-chartjs-2";

import InfoLink from "@/containers/InfoLink";
import { useI18n } from "@/contexts/i18nContext";

import { BaseWidget } from "./BaseWidget";
import {
  barOptions,
  eventPropsForPie,
  eventPropsForTimeline,
  lineOptions,
  pieOptions,
} from "./chart_options";
import { CountText } from "./CountText";
import {
  getLabelsAndDatasetsForLineChart,
  getLabelsAndDatasetsForPieChart,
  sumArray,
} from "./helpers";

const hasData = (data) =>
  data &&
  data.map((d) => (d.prValuePerWeek || []).length).reduce(sumArray, 0) > 0;

// ------------------------------------------------------------------------------------

export const PrValueInfoLink = () => (
  <InfoLink
    href={L.HELP_ARTICLE_PR_VALUE}
    content={L.MENTIONS_PR_VALUE_HOW_CALCULATED}
  />
);

// ------------------------------------------------------------------------------------

const PRValueLineWidget = (props) => {
  const isStacked = props.chartType == "area_stacked";
  const chartData = getLabelsAndDatasetsForLineChart(
    "prValuePerWeek",
    hasData(props.data) ? props.data : null,
    isStacked
  );
  const { currency } = useI18n();
  const showCurrencyLabel = true;

  const options = lineOptions(
    isStacked,
    props.size,
    showCurrencyLabel,
    currency
  );

  return (
    <BaseWidget
      {...props}
      extraHeader={<PrValueInfoLink />}
      hasData={hasData(props.data)}
    >
      <Line
        data={chartData}
        options={{ ...options, ...eventPropsForTimeline(chartData, props) }}
      />
    </BaseWidget>
  );
};

const PRValueBarWidget = (props) => {
  const isStacked = false;
  const chartData = getLabelsAndDatasetsForLineChart(
    "prValuePerWeek",
    hasData(props.data) ? props.data : null,
    isStacked
  );

  return (
    <BaseWidget
      {...props}
      extraHeader={<PrValueInfoLink />}
      hasData={hasData(props.data)}
    >
      <Bar
        data={chartData}
        options={{
          ...barOptions(true, props.size),
          ...eventPropsForTimeline(chartData, props),
        }}
      />
    </BaseWidget>
  );
};

const PRValuePieWidget = (props) => {
  const chartData = getLabelsAndDatasetsForPieChart(
    "prValuePerWeek",
    hasData(props.data) ? props.data : null,
    true // convert to percentage
  );
  const options = pieOptions();

  return (
    <BaseWidget
      {...props}
      extraHeader={<PrValueInfoLink />}
      hasData={hasData(props.data)}
    >
      <Doughnut
        data={chartData}
        options={{ ...options, ...eventPropsForPie(chartData, props) }}
      />
    </BaseWidget>
  );
};

const PRValueSumWidget = (props) => {
  const chartData = getLabelsAndDatasetsForPieChart(
    "prValuePerWeek",
    hasData(props.data) ? props.data : null
  );

  const sum = chartData.datasets
    .map((i) => i.data.reduce(sumArray, 0))
    .reduce(sumArray, 0);

  return (
    <BaseWidget
      {...props}
      extraHeader={<PrValueInfoLink />}
      hasData={hasData(props.data)}
    >
      <CountText
        testId="pr-value-sum"
        extraHeader={<PrValueInfoLink />}
        count={sum}
        isCurrency={true}
        {...props}
      />
    </BaseWidget>
  );
};

const PRValueWidget = (props) => {
  return props.chartType == "pie" ? (
    <PRValuePieWidget {...props} />
  ) : props.chartType == "bar" ? (
    <PRValueBarWidget {...props} />
  ) : props.chartType == "sum" ? (
    <PRValueSumWidget {...props} />
  ) : (
    <PRValueLineWidget {...props} />
  );
};

export { PRValueWidget };
